/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';
import { autoJoinWinrOfficial, isWinrOwnerIdentity, loadProfile } from '@/features/auth/api/session';

interface AuthState {
  session: Session | null;
  /** Raw Supabase auth user. */
  user: User | null;
  /** The `profiles` row — the OG's real "currentUser". Drives the whole app. */
  profile: Profile | null;
  /** Whether this identity owns THE WINR community (OG ownership binding). */
  isWinrOwner: boolean;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isWinrOwner, setIsWinrOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  /** Guard so we only run the join side effect once per signed-in user. */
  const joinedFor = useRef<string | null>(null);

  /**
   * Hydrate the profile for an auth user and run first-sign-in side effects
   * (auto-join WINR Official). Mirrors OG `_loadProfileForAuthUser` +
   * `onAuthStateChange` SIGNED_IN handler (js/supabase.js:185,234).
   */
  const hydrate = useCallback(async (nextSession: Session | null) => {
    setSession(nextSession);
    const authUser = nextSession?.user ?? null;

    if (!authUser) {
      setProfile(null);
      setIsWinrOwner(false);
      joinedFor.current = null;
      return;
    }

    setIsWinrOwner(isWinrOwnerIdentity(authUser));
    setProfile(await loadProfile(authUser.id));

    if (joinedFor.current !== authUser.id) {
      joinedFor.current = authUser.id;
      await autoJoinWinrOfficial();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(async ({ data }) => {
      if (cancelled) return;
      await hydrate(data.session);
      if (!cancelled) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!cancelled) void hydrate(nextSession);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [hydrate]);

  const refreshProfile = useCallback(async () => {
    if (session?.user) setProfile(await loadProfile(session.user.id));
  }, [session]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        isWinrOwner,
        loading,
        refreshProfile,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** Access the current auth session anywhere in the tree. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
